import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import type { CrmMyData } from '@aviaconsole/shared';
import { api } from '../api/client';
import { useI18n } from '../i18n';
import { Button, Card, Input, Loading } from '../components/ui';
import { MyDataView } from '../components/MyDataView';

/**
 * The public "My data" page (AVI-13 §8a), outside the console's sign-in:
 * /my-data/:slug asks for an email address, /my-data/:slug/:token is the
 * page the emailed link opens.
 */
export function PublicMyData() {
  const { slug = '', token } = useParams();
  const { t, lang, setLang } = useI18n();
  return (
    <div className="publicpage">
      <div className="stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0 }}>{t('myDataTitle')}</h1>
          <Button size="sm" onClick={() => setLang(lang === 'en' ? 'nl' : 'en')}>{lang === 'en' ? 'NL' : 'EN'}</Button>
        </div>
        {token ? <LinkView slug={slug} token={token} /> : <RequestForm slug={slug} />}
      </div>
    </div>
  );
}

function RequestForm({ slug }: { slug: string }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => api<{ message: string }>(`/api/public/my-data/${encodeURIComponent(slug)}/request`, { method: 'POST', json: { email } }) });
  return (
    <Card>
      <h2 style={{ marginTop: 0 }}>{t('myDataRequestTitle')}</h2>
      <p className="secondary">{t('myDataRequestSub')}</p>
      {send.data ? <p role="status">{t('myDataSent')}</p> : (
        <form className="stack md" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
          <Input label={t('myDataEmail')} type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          {send.error && <p className="small" style={{ color: 'var(--danger)' }}>{(send.error as Error).message}</p>}
          <Button variant="primary" type="submit" disabled={send.isPending}>{t('myDataSend')}</Button>
        </form>
      )}
    </Card>
  );
}

function LinkView({ slug, token }: { slug: string; token: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const base = `/api/public/my-data/link/${encodeURIComponent(token)}`;
  const data = useQuery({ queryKey: ['public-my-data', token], retry: false, queryFn: () => api<CrmMyData>(base) });
  const act = useMutation({
    mutationFn: (what: 'opt-out' | 'erasure-request') => api<CrmMyData>(`${base}/${what}`, { method: 'POST' }),
    onSuccess: (d) => qc.setQueryData(['public-my-data', token], d),
  });
  if (data.error) {
    return <Card><p>{t('myDataLinkExpired')}</p><a className="linkbtn" href={`/my-data/${encodeURIComponent(slug)}`}>{t('myDataRequestAgain')}</a></Card>;
  }
  if (!data.data) return <Loading />;
  const email = data.data.records[0]?.email ?? '';
  return (
    <>
      <p className="secondary">{t('myDataSub', { company: data.data.companyName })}</p>
      <MyDataView data={data.data} email={email} pending={act.isPending} onOptOut={() => act.mutate('opt-out')} onRequestErasure={() => act.mutate('erasure-request')} />
    </>
  );
}
